import type {
  HeroMatchList,
  InsightReport,
  LeagueReport,
  MatchDetail,
  LeagueSummary,
  LeagueSeriesList,
  LeagueTeamReport,
  LeagueH2HReport,
  PlayerOverview,
  Position,
  ProfileScope,
  ScoutBanBoard,
  ScoutDivisionReport,
  ScoutTeamReport,
} from '@dota/shared';

/**
 * Typed API client.
 *
 * Response types come from the shared package, so a change to a server payload
 * is a compile error in the UI rather than a runtime surprise.
 */

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown> | undefined;

  constructor(status: number, code: string, message: string, details?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/**
 * Base URL for the API.
 *
 * Empty by default, so requests are same-origin and the dev-server proxy (or a
 * reverse proxy in production) handles routing. Set `VITE_API_ORIGIN` at build
 * time when the API is deployed on a different origin to the web app — without
 * it, a separately-hosted frontend would issue same-origin `/api/...` calls
 * that hit the static host and 404, which is the entire reason the CORS and
 * cookie configuration on the server exists.
 */
const API_BASE = (import.meta.env['VITE_API_ORIGIN'] ?? '').replace(/\/$/, '');

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    // Session cookie must ride along for authenticated endpoints.
    credentials: 'include',
    headers: {
      accept: 'application/json',
      ...(init.body ? { 'content-type': 'application/json' } : {}),
      ...init.headers,
    },
  });

  const text = await response.text();

  /*
   * Parse defensively.
   *
   * Not every response comes from this API: a load balancer, proxy or CDN in
   * front of it will happily return an HTML 502 or a plain-text 504. Calling
   * JSON.parse directly threw a SyntaxError from inside the client, replacing a
   * meaningful "the server is down" with a parser error that looks like a
   * frontend bug.
   */
  let body: unknown = null;
  if (text.length > 0) {
    try {
      body = JSON.parse(text);
    } catch {
      if (response.ok) {
        throw new ApiRequestError(
          response.status,
          'invalid_response',
          'The server returned a response that could not be understood.',
        );
      }
      body = null;
    }
  }

  if (!response.ok) {
    const errorBody = body as { error?: { code?: string; message?: string; details?: Record<string, unknown> } } | null;
    throw new ApiRequestError(
      response.status,
      errorBody?.error?.code ?? 'unknown_error',
      errorBody?.error?.message ?? `Request failed with status ${response.status}`,
      errorBody?.error?.details,
    );
  }

  return body as T;
}

export interface JobStatus {
  id: string;
  type: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  progress: number;
  attempts: number;
  error: string | null;
  createdAt: string;
  completedAt: string | null;
}

export interface JobAccepted {
  status: 'queued' | 'already_running';
  job: { id: string; status: string; progress: number };
}

export interface PlayerMatchRow {
  matchId: number;
  heroId: number;
  position: number | null;
  win: boolean;
  kills: number;
  deaths: number;
  assists: number;
  goldPerMin: number | null;
  xpPerMin: number | null;
  lastHits: number | null;
  heroDamage: number | null;
  netWorth: number | null;
  startTime: string;
  duration: number;
  gameMode: number | null;
  lobbyType: number | null;
  parsed: boolean;
  /** Null when the list row never carried it. Shown as a badge when > 1. */
  partySize: number | null;
  /**
   * How this game scored, 0-100, on the same scale as the profile's headline
   * performance score. Null when the match is not benchmarkable (Turbo,
   * abandons, sub-10-minute) or no benchmark data is cached for the hero yet.
   */
  score: number | null;
}

/** Item id, display name and CDN image slug. */
export interface Item {
  id: number;
  name: string;
  slug: string;
}

/** Ability id, display name and CDN image slug. */
export interface Ability {
  id: number;
  name: string;
  slug: string;
}

export interface Hero {
  id: number;
  name: string;
  /** Valve image slug, e.g. "antimage". Derived server-side. */
  slug: string;
  primaryAttr: string;
  attackType: string;
  roles: string[];
}

export interface SessionUser {
  id: string;
  steamId: string;
  accountId: number;
  personaName: string | null;
  avatar: string | null;
  tier: string;
}

export interface ApiKeySummary {
  id: string;
  name: string;
  prefix: string;
  tier: string;
  active: boolean;
  rateLimitPerMinute: number;
  monthlyQuota: number;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  usageThisMonth: number;
}

export interface Plan {
  id: string;
  label: string;
  rateLimitPerMinute: number;
  monthlyQuota: number;
  priceUsdPerMonth: number;
}

export interface RatingModelDoc {
  positions: Array<{ position: number; label: string }>;
  metrics: Array<{
    key: string;
    label: string;
    unit: string;
    description: string;
    lowerIsBetter: boolean;
    requiresParsedReplay: boolean;
    weights: Record<string, number>;
  }>;
  methodology: {
    standardization: string;
    shrinkage: string;
    opponentAdjustment: string;
    valueOverExpected: string;
    caveats: string[];
  };
}

/**
 * Query string for the player endpoints.
 *
 * `leagueId` narrows the whole analysis — overview, insights and match list —
 * to one league, so all three must carry it or the page would show league
 * matches under a rating derived from every pub game. `profileScope` (pubs /
 * league / all) narrows it the same way, for the same reason.
 */
function scope(limit: number, leagueId?: number, profileScope: ProfileScope = 'all'): string {
  const params = new URLSearchParams({ limit: String(limit) });
  if (leagueId != null) params.set('leagueId', String(leagueId));
  if (profileScope !== 'all') params.set('scope', profileScope);
  return params.toString();
}

function heroMatchQuery(filters: {
  hero?: string | null;
  position?: number | null;
  leagueId?: number;
  profileScope?: ProfileScope;
  limit?: number;
}): string {
  const params = new URLSearchParams();
  if (filters.hero) params.set('hero', filters.hero);
  if (filters.position != null) params.set('position', String(filters.position));
  if (filters.leagueId != null) params.set('leagueId', String(filters.leagueId));
  if (filters.profileScope != null && filters.profileScope !== 'all') {
    params.set('scope', filters.profileScope);
  }
  if (filters.limit != null) params.set('limit', String(filters.limit));
  return params.toString();
}

function divisionQuery(division?: string | null): string {
  if (division == null || division.length === 0) return '';
  return `&division=${encodeURIComponent(division)}`;
}

export const api = {
  playerOverview: (accountId: number, limit = 100, leagueId?: number, profileScope?: ProfileScope) =>
    request<PlayerOverview>(`/api/players/${accountId}?${scope(limit, leagueId, profileScope)}`),

  /** Never pubs/league-scoped: the server computes insights from all games. */
  playerInsights: (accountId: number, limit = 100, leagueId?: number) =>
    request<InsightReport>(`/api/players/${accountId}/insights?${scope(limit, leagueId)}`),

  playerMatches: (accountId: number, limit = 100, leagueId?: number, profileScope?: ProfileScope) =>
    request<{ accountId: number; matches: PlayerMatchRow[] }>(
      `/api/players/${accountId}/matches?${scope(limit, leagueId, profileScope)}`,
    ),

  refreshPlayer: (accountId: number) =>
    request<JobAccepted>(`/api/players/${accountId}/refresh`, { method: 'POST' }),

  /**
   * Resume a history import from wherever this player's walk reached.
   *
   * No longer used by the UI — a refresh walks the whole history on its own —
   * and no longer "the next page": the job it starts CHAINS, so one call is
   * worth up to twenty pages rather than one. Kept because the endpoint is
   * public API, but do not reintroduce it as a button without re-reading what
   * it now costs.
   */
  importOlderMatches: (accountId: number) =>
    request<JobAccepted & { offset: number }>(`/api/players/${accountId}/history`, {
      method: 'POST',
    }),

  job: (jobId: string) => request<JobStatus>(`/api/jobs/${jobId}`),

  leagues: () =>
    request<{ leagues: Array<{ leagueId: number; name: string; tier: string | null; matchCount: number; matchesImported: number; importStatus: string; lastImportedAt: string | null }> }>(
      '/api/leagues',
    ),

  leagueReport: (leagueId: number, minMatches = 4, division?: string | null) =>
    request<LeagueReport>(
      `/api/leagues/${leagueId}?minMatches=${minMatches}${divisionQuery(division)}`,
    ),

  leagueStatus: (leagueId: number) => request<LeagueSummary>(`/api/leagues/${leagueId}/status`),

  leagueTeam: (leagueId: number, teamId: number) =>
    request<LeagueTeamReport>(`/api/leagues/${leagueId}/teams/${teamId}`),

  /**
   * Games these two teams played against each other in this league.
   *
   * Query keys match the shareable page URL (`?tab=h2h&a=&b=`).
   */
  leagueH2h: (leagueId: number, teamA: number, teamB: number, division?: string | null) =>
    request<LeagueH2HReport>(
      `/api/leagues/${leagueId}/h2h?a=${teamA}&b=${teamB}${divisionQuery(division)}`,
    ),

  /**
   * League / team / player match lists filtered by hero slug (or id) and
   * optional stored position. Query keys match the shareable page URL.
   */
  leagueMatches: (
    leagueId: number,
    filters: { hero?: string | null; position?: number | null; limit?: number; division?: string | null } = {},
  ) =>
    request<HeroMatchList>(
      `/api/leagues/${leagueId}/matches?${heroMatchQuery(filters)}${divisionQuery(filters.division)}`,
    ),

  leagueTeamMatches: (
    leagueId: number,
    teamId: number,
    filters: { hero?: string | null; position?: number | null; limit?: number } = {},
  ) =>
    request<HeroMatchList>(
      `/api/leagues/${leagueId}/teams/${teamId}/matches?${heroMatchQuery(filters)}`,
    ),

  leagueSeries: (leagueId: number, division?: string | null) => {
    const query = divisionQuery(division).replace(/^&/, '');
    const suffix = query.length > 0 ? `?${query}` : '';
    return request<LeagueSeriesList>(`/api/leagues/${leagueId}/series${suffix}`);
  },

  setMatchDivision: (leagueId: number, matchId: number, division: string | null) =>
    request<{ matchId: number; division: string | null }>(
      `/api/leagues/${leagueId}/matches/${matchId}/division`,
      { method: 'PUT', body: JSON.stringify({ division }) },
    ),

  leagueTeamSeries: (leagueId: number, teamId: number) =>
    request<LeagueSeriesList>(`/api/leagues/${leagueId}/teams/${teamId}/series`),

  playerHeroMatches: (
    accountId: number,
    filters: {
      hero?: string | null;
      position?: number | null;
      leagueId?: number;
      profileScope?: ProfileScope;
      limit?: number;
    } = {},
  ) =>
    request<HeroMatchList>(`/api/players/${accountId}/hero-matches?${heroMatchQuery(filters)}`),

  importLeague: (leagueId: number) =>
    request<JobAccepted>(`/api/leagues/${leagueId}/import`, { method: 'POST' }),

  scoutDivision: (divisionSlug: string) =>
    request<ScoutDivisionReport>(`/api/scout/rd2l/s39/${encodeURIComponent(divisionSlug)}`),

  refreshScoutDivision: (divisionSlug: string) =>
    request<JobAccepted>(`/api/scout/rd2l/s39/${encodeURIComponent(divisionSlug)}/refresh`, {
      method: 'POST',
    }),

  scoutTeam: (divisionSlug: string, teamSlug: string) =>
    request<ScoutTeamReport>(
      `/api/scout/rd2l/s39/${encodeURIComponent(divisionSlug)}/teams/${encodeURIComponent(teamSlug)}`,
    ),

  refreshScoutTeam: (divisionSlug: string, teamSlug: string) =>
    request<JobAccepted>(
      `/api/scout/rd2l/s39/${encodeURIComponent(divisionSlug)}/teams/${encodeURIComponent(teamSlug)}/refresh`,
      { method: 'POST' },
    ),

  assignScoutPosition: (
    divisionSlug: string,
    teamSlug: string,
    body: { accountId?: number | null; displayName?: string | null; assignedPosition: Position | null },
  ) =>
    request<ScoutTeamReport>(
      `/api/scout/rd2l/s39/${encodeURIComponent(divisionSlug)}/teams/${encodeURIComponent(teamSlug)}/roster/position`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),

  scoutTeamBans: (divisionSlug: string, teamSlug: string, vs?: string | null) => {
    const query = vs ? `?vs=${encodeURIComponent(vs)}` : '';
    return request<ScoutBanBoard>(
      `/api/scout/rd2l/s39/${encodeURIComponent(divisionSlug)}/teams/${encodeURIComponent(teamSlug)}/bans${query}`,
    );
  },

  scoutTeamMatches: (
    divisionSlug: string,
    teamSlug: string,
    filters: { hero?: string | null; position?: number | null; limit?: number } = {},
  ) =>
    request<HeroMatchList>(
      `/api/scout/rd2l/s39/${encodeURIComponent(divisionSlug)}/teams/${encodeURIComponent(teamSlug)}/matches?${heroMatchQuery(filters)}`,
    ),

  heroes: () => request<{ heroes: Hero[] }>('/api/meta/heroes'),

  ratingModel: () => request<RatingModelDoc>('/api/meta/rating-model'),

  me: () => request<{ authenticated: boolean; user: SessionUser | null }>('/api/auth/me'),

  logout: () => request<{ ok: boolean }>('/api/auth/logout', { method: 'POST' }),

  apiKeys: () => request<{ keys: ApiKeySummary[] }>('/api/keys'),

  createApiKey: (name: string) =>
    request<{ key: { id: string; name: string; prefix: string; key: string; tier: string }; warning: string }>(
      '/api/keys',
      { method: 'POST', body: JSON.stringify({ name }) },
    ),

  revokeApiKey: (keyId: string) => request<{ ok: boolean }>(`/api/keys/${keyId}`, { method: 'DELETE' }),

  plans: () => request<{ plans: Plan[]; keyCreationEnabled: boolean }>('/api/plans'),

  match: (matchId: number) => request<MatchDetail>(`/api/matches/${matchId}`),

  /**
   * Fetch a match's full detail on demand and return the completed match.
   *
   * Returns the whole match rather than an acknowledgement, so the page can
   * swap it straight into the cache instead of refetching what the server just
   * built.
   */
  fetchMatchDetail: (matchId: number) =>
    request<MatchDetail>(`/api/matches/${matchId}/fetch`, { method: 'POST' }),

  /**
   * Ask OpenDota to parse this match's replay.
   *
   * Returns as soon as the request is queued on their side, not when parsing
   * finishes — it runs on their queue, takes minutes, and we have no job of our
   * own to poll. The caller has to tell the user to come back.
   */
  requestMatchParse: (matchId: number) =>
    request<{ status: 'requested'; jobId: number | null }>(`/api/matches/${matchId}/parse`, {
      method: 'POST',
    }),

  items: () => request<{ items: Item[] }>('/api/meta/items'),

  abilities: () => request<{ abilities: Ability[] }>('/api/meta/abilities'),
};
